import { Controller } from "@hotwired/stimulus"

// ── Constants ──────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  online:       { label: 'Online',   dot: 'bg-green-500',  badge: 'bg-green-100 text-green-800' },
  paused:       { label: 'Pausado',  dot: 'bg-yellow-400', badge: 'bg-yellow-100 text-yellow-800' },
  off_schedule: { label: 'Offline',  dot: 'bg-gray-400',   badge: 'bg-gray-100 text-gray-600' },
  inactive:     { label: 'Inativo',  dot: 'bg-red-400',    badge: 'bg-red-100 text-red-700' }
}

const STATUS_ORDER = { online: 0, paused: 1, off_schedule: 2, inactive: 3 }

const AVATAR_PALETTE = [
  ['#EFF6FF', '#3B82F6'], ['#F0FDF4', '#22C55E'], ['#FFF7ED', '#F97316'],
  ['#FDF4FF', '#A855F7'], ['#FFF1F2', '#F43F5E'], ['#F0FDFA', '#14B8A6'],
  ['#FEFCE8', '#CA8A04'], ['#F8FAFC', '#64748B']
]

// ── Helpers ────────────────────────────────────────────────────────────────

function avatarColors(name) {
  let h = 0
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h)
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length]
}

function initials(name) {
  const parts = (name || '?').trim().split(/\s+/)
  return parts.length === 1
    ? parts[0].slice(0, 2).toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function urgencyConfig(queuedAt) {
  const mins = (Date.now() - new Date(queuedAt).getTime()) / 60000
  if (mins > 60)  return { text: 'text-red-600',   dot: 'bg-red-500' }
  if (mins > 15)  return { text: 'text-amber-600', dot: 'bg-amber-500' }
  return              { text: 'text-gray-400',   dot: 'bg-gray-300' }
}

// ── Controller ─────────────────────────────────────────────────────────────

export default class extends Controller {
  static targets = ['dialog', 'body', 'loading', 'empty', 'statsBar', 'genericStat', 'reservedStat']

  connect() {
    this._usersCache = new Map()
  }

  // ── Modal lifecycle ──────────────────────────────────────────────────────

  async open(event) {
    const btn = event.currentTarget
    const rotationId = btn.dataset.pendingQueueModalRotationIdValue
    const adminId    = btn.dataset.pendingQueueModalAdminIdValue
    const focus      = btn.dataset.pendingQueueModalFocusValue

    this._rotationId = rotationId

    this.showLoading()
    this.dialogTarget.showModal()

    try {
      const res = await fetch(
        `/real_estate_admin/${adminId}/support_assignment_rotations/${rotationId}/pending_assignments`,
        { headers: { Accept: 'application/json', 'X-CSRF-Token': this.csrfToken } }
      )
      if (!res.ok) throw new Error(res.statusText)
      this.render(await res.json(), focus)
    } catch {
      this.loadingTarget.classList.add('hidden')
      this.bodyTarget.innerHTML = this.errorState('Erro ao carregar a fila.')
    }
  }

  close() { this.dialogTarget.close() }

  closeOnBackdrop(event) { if (event.target === this.dialogTarget) this.close() }

  // ── Transfer panel ────────────────────────────────────────────────────────

  async toggleTransferPanel(event) {
    const btn = event.currentTarget
    const { conversationId, pendingId } = btn.dataset

    // Close any other open panels first
    this.bodyTarget.querySelectorAll('.transfer-panel').forEach(p => {
      if (p.dataset.pendingId !== pendingId) this.collapsePanel(p)
    })

    const existingPanel = document.getElementById(`transfer-panel-${pendingId}`)
    if (existingPanel) {
      this.collapsePanel(existingPanel)
      return
    }

    const row = document.getElementById(`pending-row-${pendingId}`)
    if (!row) return

    const panel = this.buildTransferPanel(pendingId, conversationId)
    row.insertAdjacentElement('afterend', panel)

    // Double-rAF: first frame registers the initial max-height:0,
    // second frame triggers the transition to the real height.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.style.maxHeight = panel.scrollHeight + 128 + 'px' // +128 for user list that loads async
        panel.style.opacity = '1'
      })
    })

    await this.loadTransferUsers(panel, conversationId)
  }

  async loadTransferUsers(panel, conversationId) {
    const listEl = panel.querySelector('.transfer-user-list')

    if (this._usersCache.has(conversationId)) {
      this.renderTransferList(listEl, this._usersCache.get(conversationId), panel)
      return
    }

    listEl.innerHTML = `
      <div class="flex items-center justify-center py-4">
        <div class="w-4 h-4 rounded-full border-2 border-gray-200 border-t-yellow-400 animate-spin"></div>
      </div>`

    try {
      const res = await fetch(`/api/v1/conversations/${conversationId}/assignable_users`, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      })
      if (!res.ok) throw new Error()
      const data = await res.json()
      const payload = { users: data.data || [], roles: data.roles || [] }
      this._usersCache.set(conversationId, payload)
      this.renderTransferList(listEl, payload, panel)
    } catch {
      listEl.innerHTML = `<p class="px-3 py-3 text-xs text-center text-red-400">Erro ao carregar usuários.</p>`
    }
  }

  renderTransferList(listEl, { users, roles }, panel) {
    const searchEl = panel.querySelector('.transfer-search')
    const query    = (searchEl?.value || '').toLowerCase()

    const sortedUsers = [...users].sort((a, b) =>
      (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9) ||
      (a.name || '').localeCompare(b.name || '', 'pt-BR')
    )

    const filteredUsers = query
      ? sortedUsers.filter(u => u.name.toLowerCase().includes(query) || u.email.toLowerCase().includes(query))
      : sortedUsers

    const filteredRoles = query
      ? roles.filter(r => r.name.toLowerCase().includes(query))
      : [...roles].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))

    if (!filteredUsers.length && !filteredRoles.length) {
      listEl.innerHTML = `<p class="px-3 py-3 text-xs text-center text-gray-400">Nenhum resultado encontrado</p>`
      return
    }

    let html = ''

    if (filteredRoles.length) {
      html += `<div class="sticky top-0 bg-gray-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-100">Perfis</div>`
      filteredRoles.forEach(role => {
        html += `
          <button type="button"
            class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-gray-50 transition-colors"
            data-transfer-type="role"
            data-transfer-id="${this.escape(role.id)}"
            data-transfer-name="${this.escape(role.name)}"
            data-transfer-status=""
          >
            ${this.groupIcon()}
            <span class="truncate font-medium">${this.escape(role.name)}</span>
          </button>`
      })
    }

    if (filteredRoles.length && filteredUsers.length) {
      html += `<div class="border-t border-gray-100"></div>`
    }

    if (filteredUsers.length) {
      html += `<div class="sticky top-0 bg-gray-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-100">Usuários</div>`
      filteredUsers.forEach(user => {
        const dot = STATUS_CONFIG[user.status]?.dot || 'bg-gray-400'
        html += `
          <button type="button"
            class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-gray-50 transition-colors"
            data-transfer-type="user"
            data-transfer-id="${this.escape(user.id)}"
            data-transfer-name="${this.escape(user.name)}"
            data-transfer-status="${this.escape(user.status)}"
          >
            <span class="w-2 h-2 rounded-full flex-shrink-0 ${dot}"></span>
            <div class="min-w-0 flex-1">
              <span class="block truncate">${this.escape(user.name)}</span>
              ${user.name !== user.email ? `<span class="block truncate text-[10px] text-gray-400">${this.escape(user.email)}</span>` : ''}
            </div>
          </button>`
      })
    }

    listEl.innerHTML = html

    // Wire up selection
    listEl.querySelectorAll('button[data-transfer-type]').forEach(btn => {
      btn.addEventListener('click', () => this.selectTransferTarget(panel, btn))
    })
  }

  selectTransferTarget(panel, btn) {
    const { transferType, transferId, transferName, transferStatus } = btn.dataset
    const dot = STATUS_CONFIG[transferStatus]?.dot || 'bg-gray-400'

    // Highlight selected
    panel.querySelectorAll('button[data-transfer-type]').forEach(b => {
      b.classList.remove('bg-yellow-50', 'ring-1', 'ring-inset', 'ring-yellow-300')
    })
    btn.classList.add('bg-yellow-50', 'ring-1', 'ring-inset', 'ring-yellow-300')

    const chipEl = panel.querySelector('.transfer-chip')
    chipEl.classList.remove('hidden')
    chipEl.innerHTML = `
      <div class="flex items-center gap-1.5 min-w-0 flex-1 bg-yellow-50 rounded-lg px-2.5 py-1.5 ring-1 ring-inset ring-yellow-300">
        ${transferType === 'role'
          ? this.groupIcon()
          : `<span class="w-2 h-2 rounded-full flex-shrink-0 ${dot}"></span>`}
        <span class="truncate text-xs font-medium text-gray-800">${this.escape(transferName)}</span>
        <button type="button" class="ml-auto flex-shrink-0 text-gray-400 hover:text-gray-600 clear-selection">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      <button type="button"
        class="confirm-transfer flex-shrink-0 rounded-lg px-3 py-1.5 text-xs font-bold text-black shadow-sm transition-all hover:shadow-md"
        style="background: linear-gradient(135deg, #FFDE45 0%, #FFD700 100%);"
        data-transfer-type="${this.escape(transferType)}"
        data-transfer-id="${this.escape(transferId)}"
        data-transfer-name="${this.escape(transferName)}"
        data-pending-id="${panel.dataset.pendingId}"
        data-conversation-id="${panel.dataset.conversationId}"
      >Transferir</button>
    `

    chipEl.querySelector('.clear-selection').addEventListener('click', () => {
      chipEl.classList.add('hidden')
      chipEl.innerHTML = ''
      panel.querySelectorAll('button[data-transfer-type]').forEach(b => {
        b.classList.remove('bg-yellow-50', 'ring-1', 'ring-inset', 'ring-yellow-300')
      })
    })

    chipEl.querySelector('.confirm-transfer').addEventListener('click', (e) => {
      this.confirmTransfer(e.currentTarget)
    })

    // Scroll chip into view
    chipEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  async confirmTransfer(confirmBtn) {
    const { transferType, transferId, pendingId, conversationId } = confirmBtn.dataset

    confirmBtn.disabled = true
    confirmBtn.innerHTML = `<div class="w-3.5 h-3.5 rounded-full border-2 border-black/20 border-t-black animate-spin mx-auto"></div>`

    const body = transferType === 'role'
      ? { role_id: transferId }
      : { user_id: transferId }

    try {
      const res = await fetch(`/api/v1/conversations/${conversationId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': this.csrfToken },
        body: JSON.stringify(body)
      })

      if (res.ok) {
        // Close panel then fade out both the panel and row together
        const panel = document.getElementById(`transfer-panel-${pendingId}`)
        const row   = document.getElementById(`pending-row-${pendingId}`)

        ;[panel, row].forEach(el => {
          if (!el) return
          el.style.transition = 'opacity 220ms, max-height 220ms, padding 220ms'
          el.style.opacity = '0'
          el.style.maxHeight = '0'
          el.style.overflow = 'hidden'
          el.style.paddingTop = '0'
          el.style.paddingBottom = '0'
        })

        setTimeout(() => {
          panel?.remove()
          row?.remove()
          this.updateAfterTransfer()
        }, 230)

        this.showToast('Conversa transferida com sucesso', 'success')
      } else {
        const data = await res.json().catch(() => ({}))
        const msg  = data.error || 'Erro ao transferir conversa'
        this.showToast(msg, 'error')
        confirmBtn.disabled = false
        confirmBtn.textContent = 'Transferir'
      }
    } catch {
      this.showToast('Erro de conexão. Tente novamente.', 'error')
      confirmBtn.disabled = false
      confirmBtn.textContent = 'Transferir'
    }
  }

  // ── Rendering ─────────────────────────────────────────────────────────────

  render(data, focus) {
    this.loadingTarget.classList.add('hidden')
    this.bodyTarget.innerHTML = ''

    const totalGeneric  = data.generic.length
    const totalReserved = data.reserved.reduce((n, g) => n + g.pendings.length, 0)

    if (!totalGeneric && !totalReserved) {
      this.emptyTarget.classList.remove('hidden')
      this.statsBarTarget.classList.add('hidden')
      return
    }

    this.emptyTarget.classList.add('hidden')
    this.updateStats(totalGeneric, totalReserved)

    if (totalGeneric > 0) {
      this.bodyTarget.appendChild(this.buildSection({
        id: 'generic-section',
        icon: this.clockIcon(),
        title: 'Fila Geral',
        count: totalGeneric,
        accentBg: 'bg-amber-50',
        accentBorder: 'border-amber-200',
        accentText: 'text-amber-800',
        pendings: data.generic
      }))
    }

    data.reserved.forEach(group => {
      const cfg = STATUS_CONFIG[group.user.status] || STATUS_CONFIG.off_schedule
      this.bodyTarget.appendChild(this.buildSection({
        id: `reserved-section-${group.user.id}`,
        icon: this.userAvatar(group.user.name),
        title: group.user.name,
        subtitle: 'Reservadas',
        count: group.pendings.length,
        statusDot: cfg.dot,
        statusLabel: cfg.label,
        statusBadge: cfg.badge,
        accentBg: 'bg-violet-50',
        accentBorder: 'border-violet-200',
        accentText: 'text-violet-800',
        pendings: group.pendings
      }))
    })

    if (focus === 'reserved' && data.reserved.length > 0) {
      requestAnimationFrame(() => {
        this.bodyTarget.querySelector('[id^="reserved-section-"]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
  }

  buildSection({ id, icon, title, subtitle, count, statusDot, statusLabel, statusBadge, accentBg, accentBorder, accentText, pendings }) {
    const section = document.createElement('div')
    section.id = id
    section.className = 'border-b border-gray-100 last:border-0'

    const header = document.createElement('div')
    header.className = `flex items-center gap-2.5 px-5 py-2.5 border-b ${accentBg} ${accentBorder}`
    header.innerHTML = `
      ${icon}
      <div class="flex-1 min-w-0 flex items-center gap-1.5">
        ${subtitle ? `<span class="text-[10px] font-medium text-gray-400 uppercase tracking-wide">${subtitle}</span><span class="text-gray-300">·</span>` : ''}
        <span class="text-xs font-semibold text-gray-800 truncate">${this.escape(title)}</span>
      </div>
      ${statusLabel ? `
        <div class="flex items-center gap-1 flex-shrink-0">
          <span class="w-1.5 h-1.5 rounded-full ${statusDot}"></span>
          <span class="text-[10px] font-medium px-1.5 py-0.5 rounded-full ${statusBadge}">${this.escape(statusLabel)}</span>
        </div>` : ''}
      <span class="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${accentText}" style="background:rgba(0,0,0,0.06);">${count}</span>
    `
    section.appendChild(header)

    pendings.forEach((p, i) => {
      const row = this.buildRow(p)
      row.style.opacity = '0'
      row.style.transform = 'translateY(4px)'
      row.style.transition = `opacity 180ms ${i * 30}ms, transform 180ms ${i * 30}ms`
      section.appendChild(row)
      requestAnimationFrame(() => { row.style.opacity = '1'; row.style.transform = 'translateY(0)' })
    })

    return section
  }

  buildRow(pending) {
    const row = document.createElement('div')
    row.id = `pending-row-${pending.id}`
    row.className = 'flex items-center gap-3 px-5 py-3 hover:bg-gray-50/80 transition-colors border-b border-gray-50 last:border-0'
    row.style.maxHeight = '200px'

    const urg = urgencyConfig(pending.queued_at)
    const [bgColor, textColor] = avatarColors(pending.client_name)

    row.innerHTML = `
      <div class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold"
           style="background:${bgColor}; color:${textColor};">${this.escape(initials(pending.client_name))}</div>

      <div class="flex-1 min-w-0">
        <p class="text-sm font-medium text-gray-900 truncate">${this.escape(pending.client_name)}</p>
        <div class="flex items-center gap-1 mt-0.5">
          <span class="w-1.5 h-1.5 rounded-full flex-shrink-0 ${urg.dot}"></span>
          <span class="text-[11px] ${urg.text} font-medium">${this.escape(pending.waiting_duration)} na fila</span>
        </div>
      </div>

      <div class="flex items-center gap-1.5 flex-shrink-0">
        <a href="${this.escape(pending.conversation_path)}" target="_blank" rel="noopener"
           class="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
          </svg>
          Ver
        </a>
        <button type="button"
          class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border border-gray-200 text-gray-600 hover:border-violet-400 hover:text-violet-700 hover:bg-violet-50 transition-all"
          data-action="click->pending-queue-modal#toggleTransferPanel"
          data-conversation-id="${pending.conversation_id}"
          data-pending-id="${pending.id}"
        >
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
          </svg>
          Transferir
        </button>
      </div>
    `

    return row
  }

  buildTransferPanel(pendingId, conversationId) {
    const panel = document.createElement('div')
    panel.id = `transfer-panel-${pendingId}`
    panel.className = 'transfer-panel border-b border-violet-100 bg-violet-50/40 overflow-hidden'
    panel.dataset.pendingId = pendingId
    panel.dataset.conversationId = conversationId
    panel.style.maxHeight = '0'
    panel.style.opacity = '0'
    panel.style.transition = 'max-height 220ms ease, opacity 180ms ease'

    panel.innerHTML = `
      <div class="px-5 pt-3 pb-1">
        <div class="relative">
          <input type="text" placeholder="Buscar por nome ou email..."
            class="transfer-search w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:border-violet-400 focus:ring-1 focus:ring-violet-200 transition-colors"
          />
          <svg class="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
        </div>
      </div>

      <div class="transfer-user-list max-h-44 overflow-y-auto mx-5 mt-1 mb-3 rounded-lg border border-gray-200 bg-white shadow-sm"></div>

      <div class="transfer-chip hidden flex items-center gap-2 px-5 pb-3"></div>
    `

    // Live search with debounce
    let debounceTimer
    const searchEl = panel.querySelector('.transfer-search')
    searchEl.addEventListener('input', () => {
      clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        const cached = this._usersCache.get(conversationId)
        if (cached) this.renderTransferList(panel.querySelector('.transfer-user-list'), cached, panel)
      }, 200)
    })

    return panel
  }

  collapsePanel(panel) {
    panel.style.maxHeight = '0'
    panel.style.opacity = '0'
    setTimeout(() => panel.remove(), 230)
  }

  // ── State helpers ─────────────────────────────────────────────────────────

  updateStats(generic, reserved) {
    this.statsBarTarget.classList.remove('hidden')
    this.statsBarTarget.classList.add('flex')
    if (generic > 0) {
      this.genericStatTarget.innerHTML = `<span class="w-1.5 h-1.5 rounded-full bg-amber-500"></span>${generic} geral`
      this.genericStatTarget.classList.remove('hidden')
    }
    if (reserved > 0) {
      this.reservedStatTarget.innerHTML = `<span class="w-1.5 h-1.5 rounded-full bg-violet-500"></span>${reserved} reservada${reserved > 1 ? 's' : ''}`
      this.reservedStatTarget.classList.remove('hidden')
    }
  }

  showLoading() {
    this.bodyTarget.innerHTML = ''
    this.emptyTarget.classList.add('hidden')
    this.statsBarTarget.classList.add('hidden')
    this.loadingTarget.classList.remove('hidden')
  }

  updateAfterTransfer() {
    // Remove sections that have no rows left
    this.bodyTarget.querySelectorAll('#generic-section, [id^="reserved-section-"]').forEach(section => {
      if (!section.querySelector('[id^="pending-row-"]')) section.remove()
    })

    const genericCount  = this.bodyTarget.querySelector('#generic-section')
      ?.querySelectorAll('[id^="pending-row-"]').length ?? 0
    const reservedCount = [...this.bodyTarget.querySelectorAll('[id^="reserved-section-"]')]
      .reduce((n, s) => n + s.querySelectorAll('[id^="pending-row-"]').length, 0)

    if (genericCount === 0 && reservedCount === 0) {
      this.bodyTarget.innerHTML = ''
      this.emptyTarget.classList.remove('hidden')
      this.statsBarTarget.classList.add('hidden')
      this.statsBarTarget.classList.remove('flex')
    } else {
      this.genericStatTarget.innerHTML  = genericCount  > 0
        ? `<span class="w-1.5 h-1.5 rounded-full bg-amber-500"></span>${genericCount} geral`
        : ''
      this.reservedStatTarget.innerHTML = reservedCount > 0
        ? `<span class="w-1.5 h-1.5 rounded-full bg-violet-500"></span>${reservedCount} reservada${reservedCount > 1 ? 's' : ''}`
        : ''
    }

    this.updatePageBadges(genericCount, reservedCount)
  }

  updatePageBadges(genericCount, reservedCount) {
    if (!this._rotationId) return
    const card = document.querySelector(`[data-rotation-id="${this._rotationId}"]`)
    if (!card) return

    const updateBtn = (btn, count, textFn) => {
      if (!btn) return
      if (count <= 0) { btn.remove(); return }
      const textNode = [...btn.childNodes].filter(n => n.nodeType === Node.TEXT_NODE).at(-1)
      if (textNode) textNode.textContent = textFn(count)
    }

    updateBtn(
      card.querySelector('[data-pending-queue-modal-focus-value="generic"]'),
      genericCount,
      n => `${n} na fila`
    )
    updateBtn(
      card.querySelector('[data-pending-queue-modal-focus-value="reserved"]'),
      reservedCount,
      n => `${n} reservada${n > 1 ? 's' : ''}`
    )

    if (genericCount <= 0 && reservedCount <= 0) {
      card.querySelector('.flex.items-center.gap-1\\.5.mt-2.pt-2.border-t')?.remove()
    }
  }

  // ── Icon helpers ──────────────────────────────────────────────────────────

  clockIcon() {
    return `<div class="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
      <svg class="w-3.5 h-3.5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>
    </div>`
  }

  userAvatar(name) {
    const [bg, color] = avatarColors(name)
    return `<div class="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold"
                 style="background:${bg}; color:${color};">${this.escape(initials(name))}</div>`
  }

  groupIcon() {
    return `<svg class="w-3.5 h-3.5 flex-shrink-0 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
      <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"/>
    </svg>`
  }

  errorState(message) {
    return `<div class="flex flex-col items-center py-12 gap-2">
      <svg class="w-8 h-8 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
      </svg>
      <p class="text-sm text-gray-500">${this.escape(message)}</p>
    </div>`
  }

  // ── Utilities ─────────────────────────────────────────────────────────────

  escape(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  showToast(message, type = 'info') {
    document.querySelectorAll('.rotation-toast').forEach(t => t.remove())

    const configs = {
      success: { bg: 'bg-gray-900', icon: 'M5 13l4 4L19 7' },
      error:   { bg: 'bg-red-600',  icon: 'M6 18L18 6M6 6l12 12' },
      info:    { bg: 'bg-gray-800', icon: null }
    }
    const cfg = configs[type] || configs.info

    const toast = document.createElement('div')
    toast.className = `rotation-toast fixed bottom-4 right-4 px-4 py-3 rounded-xl shadow-lg z-[9999] flex items-center gap-2.5 text-sm font-medium text-white opacity-0 translate-y-2 transition-all duration-300 ${cfg.bg}`
    toast.innerHTML = `
      ${cfg.icon ? `<svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="${cfg.icon}"/></svg>` : ''}
      <span>${this.escape(message)}</span>
    `

    document.body.appendChild(toast)
    requestAnimationFrame(() => toast.classList.remove('opacity-0', 'translate-y-2'))
    setTimeout(() => {
      toast.classList.add('opacity-0', 'translate-y-2')
      setTimeout(() => toast.remove(), 300)
    }, 3000)
  }
}
